import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Mail, MapPin, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_IMG = "https://customer-assets.emergentagent.com/job_airbnb-revenue-plus/artifacts/vf71z3j2_4.png";
const BOOK_A_CALL_URL = "https://calendly.com/maison-locale/intro"; // owner will update later

const propertyTypes = [
  "Villa",
  "Chalet",
  "Townhouse",
  "Beach House",
  "Loft / Apartment",
  "Boutique Portfolio",
  "Other",
];

const packageOptions = ["Essentials", "Signature", "Bespoke", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    property_location: "",
    property_type: "",
    package_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    try {
      const pkg = sessionStorage.getItem("ml_pkg");
      if (pkg) setForm((f) => ({ ...f, package_interest: pkg }));
    } catch (_) {
      /* ignore storage errors */
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Received. We reply personally within one working day.");
      setForm({
        name: "",
        email: "",
        property_location: "",
        property_type: "",
        package_interest: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* Left: image + book a call */}
        <div className="lg:col-span-5 reveal">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src={CONTACT_IMG}
              alt="Warm bedroom interior"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="mt-10 border-t border-black/15 pt-8">
            <p className="overline mb-4">Prefer a Conversation</p>
            <h3 className="font-serif-editorial text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
              Book a 30-minute
              <br />
              <span className="italic text-[#6B5C4D]">discovery call.</span>
            </h3>
            <a
              href={BOOK_A_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-call"
              className="mt-8 inline-flex items-center gap-3 h-13 py-4 px-6 bg-[#1A1A1A] text-[#FAF9F6] text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-[#A89680] transition-colors duration-500"
            >
              <CalendarDays className="w-4 h-4" strokeWidth={1.5} />
              Book a Call
            </a>

            <div className="mt-10 space-y-4 text-[#5C564D]">
              <p className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-[#A89680]" strokeWidth={1.5} />
                <a
                  href="mailto:contact@maison-locale.com"
                  data-testid="contact-email"
                  className="hover-underline"
                >
                  contact@maison-locale.com
                </a>
              </p>
              <p className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-[#A89680]" strokeWidth={1.5} />
                <a
                  href="tel:+14803228573"
                  data-testid="contact-phone"
                  className="hover-underline"
                >
                  +1 480 322 8573
                </a>
              </p>
              <p className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-[#A89680]" strokeWidth={1.5} />
                Studio · Lisbon · Milano · London
              </p>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7 reveal">
          <p className="overline mb-6">Begin an Inquiry</p>
          <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em] text-[#1A1A1A] font-light mb-10">
            Tell us about
            <br />
            <span className="italic text-[#6B5C4D]">your home.</span>
          </h2>

          {submitted ? (
            <div
              data-testid="contact-form-success"
              className="border border-[#1A1A1A]/15 p-10 bg-white"
            >
              <p className="overline mb-4">Received</p>
              <h3 className="font-serif-editorial text-3xl text-[#1A1A1A] mb-4">
                Thank you.
              </h3>
              <p className="text-[#5C564D] leading-relaxed">
                Your inquiry has been passed to a founder. Expect a personal
                reply within one working day — from a real name, not a
                form-response address.
              </p>
              <button
                type="button"
                data-testid="contact-form-reset"
                onClick={() => setSubmitted(false)}
                className="mt-8 text-[11px] tracking-[0.24em] uppercase text-[#1A1A1A] hover-underline"
              >
                Send another →
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
              noValidate
            >
              <Field label="Your name" required>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  className="ml-input"
                  autoComplete="name"
                />
              </Field>

              <Field label="Email" required>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="ml-input"
                  autoComplete="email"
                />
              </Field>

              <Field label="Property location">
                <input
                  data-testid="contact-input-location"
                  type="text"
                  value={form.property_location}
                  onChange={set("property_location")}
                  placeholder="City, country"
                  className="ml-input"
                />
              </Field>

              <Field label="Property type">
                <select
                  data-testid="contact-select-type"
                  value={form.property_type}
                  onChange={set("property_type")}
                  className="ml-input appearance-none"
                >
                  <option value="">Select —</option>
                  {propertyTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Package of interest">
                  <select
                    data-testid="contact-select-package"
                    value={form.package_interest}
                    onChange={set("package_interest")}
                    className="ml-input appearance-none"
                  >
                    <option value="">Select —</option>
                    {packageOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field label="Tell us about your home & goals" required>
                  <textarea
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    className="ml-input resize-none"
                    placeholder="What's the property? What does success look like for you?"
                  />
                </Field>
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center gap-6 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="inline-flex items-center h-14 px-10 bg-[#1A1A1A] text-[#FAF9F6] text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-[#A89680] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-500"
                >
                  {submitting ? "Sending…" : "Send Inquiry"}
                </button>
                <p className="text-xs text-[#5C564D]">
                  Replied to personally, within one working day.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .ml-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(26,26,26,0.2);
          padding: 12px 0;
          font-family: 'Outfit', sans-serif;
          font-size: 1rem;
          color: #1A1A1A;
          outline: none;
          border-radius: 0;
          transition: border-color 300ms ease;
        }
        .ml-input:focus {
          border-bottom: 2px solid #1A1A1A;
          padding-bottom: 11px;
        }
        .ml-input::placeholder { color: rgba(26,26,26,0.35); }
        select.ml-input { background-image: linear-gradient(45deg, transparent 50%, #A89680 50%), linear-gradient(135deg, #A89680 50%, transparent 50%); background-position: calc(100% - 14px) 20px, calc(100% - 9px) 20px; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 28px; }
      `}</style>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col">
      <span className="overline mb-3">
        {label}
        {required && <span className="text-[#A89680] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
